import { VisitColorTags } from './visit-color-tags.tsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi, scheduleApi } from '../../api/endpoints.ts';
import { ApiRequestError, api } from '../../api/client.ts';
import { SwapApprovalCard, type OfficeSwapListing } from '../shift-swaps.tsx';
import { Alert, Button, Checkbox, Select, TextInput } from '../ui.tsx';
import { JobProfitCard } from './job-profit-card.tsx';
import { VisitPriceCard } from './visit-price-card.tsx';
import { VisitExtraWorkCard } from './visit-extra-work-card.tsx';
import { addDaysToDate, localDateTimeToUtc } from '@shared/calculations/recurrence.ts';
import { AppointmentBadge } from './appointment-badge.tsx';
import AddressLink from '../address-link.tsx';
import { AppointmentActions } from './appointment-actions.tsx';
import { PaymentBadge } from '../payments/payment-badge.tsx';
import { MessageComposer } from '../message-composer.tsx';
import { VisitChecklist } from './visit-checklist.tsx';
import { VisitInstructionsCard } from './visit-instructions.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import { formatDuration } from './format.ts';
import { formatLocalTime, formatLongDate } from '../../i18n/datetime.ts';
import { formatMinutes } from '../../i18n/format.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { useSession } from '../../state/session.tsx';
import { invalidateQuoteViews } from '../../state/quote-cache.ts';
import { OPEN_APPOINTMENT_STATUSES } from '@shared/constants/index.ts';
import type { Appointment, TimeEntry } from '@shared/types/index.ts';

/** "850 m" under a kilometer, "1.2 km" from there — the office's own units. */
function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

/** An instant's wall clock in `timezone`: the date and HH:MM a form edits. */
function zonedParts(iso: string, timezone: string): { date: string; time: string } {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(iso));
    const at = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    return { date: `${at('year')}-${at('month')}-${at('day')}`, time: `${at('hour')}:${at('minute')}` };
  } catch {
    return { date: iso.slice(0, 10), time: iso.slice(11, 16) };
  }
}

/**
 * One stretch of time as the office edits it: a date and a clock-in and
 * clock-out time on the visit's own clock. Out before in rolls to the next
 * day — a night clean that ran past midnight.
 */
function TimeEntryForm({
  timezone,
  initial,
  members,
  busy,
  error,
  onSave,
  onCancel,
}: {
  timezone: string;
  initial: { userId?: string; date: string; inTime: string; outTime: string; unpaid: boolean };
  /** Present when adding: whose time this is. */
  members?: Array<{ id: string; fullName: string }>;
  busy: boolean;
  error: string | null;
  onSave: (value: { userId?: string; clockedInAt: string; clockedOutAt: string; unpaid: boolean }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState(initial.userId ?? members?.[0]?.id ?? '');
  const [date, setDate] = useState(initial.date);
  const [inTime, setInTime] = useState(initial.inTime);
  const [outTime, setOutTime] = useState(initial.outTime);
  const [unpaid, setUnpaid] = useState(initial.unpaid);

  const submit = () => {
    if (!date || !inTime || !outTime) return;
    const clockedInAt = localDateTimeToUtc(date, inTime, timezone);
    const outDate = outTime <= inTime ? addDaysToDate(date, 1) : date;
    const clockedOutAt = localDateTimeToUtc(outDate, outTime, timezone);
    // The team loads after the form opens, so an untouched picker means the
    // first person it lists.
    const chosen = userId || members?.[0]?.id || '';
    onSave({ ...(members ? { userId: chosen } : {}), clockedInAt, clockedOutAt, unpaid });
  };

  return (
    <div className="mt-2 rounded-md border border-[color:var(--color-line)] p-3">
      <div className="grid gap-x-3 sm:grid-cols-2">
        {members ? (
          <Select
            label={t('jobProfit.time.who')}
            value={userId || members[0]?.id || ''}
            options={members.map((member) => ({ value: member.id, label: member.fullName }))}
            onChange={(event) => setUserId(event.target.value)}
          />
        ) : null}
        <TextInput
          label={t('jobProfit.time.date')}
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
        <TextInput
          label={t('jobProfit.time.in')}
          type="time"
          value={inTime}
          onChange={(event) => setInTime(event.target.value)}
        />
        <TextInput
          label={t('jobProfit.time.out')}
          type="time"
          value={outTime}
          onChange={(event) => setOutTime(event.target.value)}
        />
      </div>
      <Checkbox
        label={t('jobProfit.time.unpaid')}
        hint={t('jobProfit.time.unpaidHint')}
        checked={unpaid}
        onChange={(event) => setUnpaid(event.target.checked)}
      />
      {error ? (
        <div className="mt-2">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      <div className="mt-2 flex gap-3">
        <Button variant="primary" busy={busy} onClick={submit}>
          {t('jobProfit.time.save')}
        </Button>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
      </div>
    </div>
  );
}

/**
 * Who clocked in and out of this visit. The capture surface is mobile; what
 * the office needs here is the record: each person's punches in the visit's
 * own timezone, how long they stayed, and whether the phone said they were
 * actually at the building when they pressed the button.
 *
 * The owner and managers can also correct it (0035) — a missed clock-out, a
 * wrong time, time nobody punched, a ride-along nobody is paid for — because
 * these are the hours pay is costed on.
 */
function TimeClockSection({
  appointmentId,
  entries,
  timezone,
  editable,
  onChanged,
}: {
  appointmentId: string;
  entries: TimeEntry[];
  timezone: string;
  editable: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const team = useQuery({ queryKey: ['team'], queryFn: () => companyApi.users(), enabled: editable && adding });

  const clock = (iso: string) =>
    new Intl.DateTimeFormat(getDateLocale(), {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
    }).format(new Date(iso));

  const settled = () => {
    setError(null);
    setEditing(null);
    setAdding(false);
    void queryClient.invalidateQueries({ queryKey: ['appointment', appointmentId] });
    onChanged();
  };
  const failed = (caught: unknown) =>
    setError(caught instanceof ApiRequestError ? caught.message : t('jobProfit.time.saveError'));

  const update = useMutation({
    mutationFn: (input: { entryId: string; clockedInAt?: string; clockedOutAt?: string; unpaid?: boolean }) => {
      const { entryId, ...body } = input;
      return scheduleApi.updateTimeEntry(appointmentId, entryId, body);
    },
    onSuccess: settled,
    onError: failed,
  });
  const add = useMutation({
    mutationFn: (body: { userId: string; clockedInAt: string; clockedOutAt: string; unpaid: boolean }) =>
      scheduleApi.addTimeEntry(appointmentId, body),
    onSuccess: settled,
    onError: failed,
  });

  // The label carries the meaning; color only reinforces it (spec §27). Null
  // is its own answer — no fix was sent, or the property has no coordinates —
  // which must not read as either "on site" or "off site".
  const badge = (entry: TimeEntry) =>
    entry.onSite === null ? (
      <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
        {t('visitManage.timeClock.noLocation')}
      </span>
    ) : entry.onSite ? (
      <span className="badge border-[color:var(--color-brand)] text-[color:var(--color-brand)]">
        {t('visitManage.timeClock.onSite')}
      </span>
    ) : (
      <span className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
        {t('visitManage.timeClock.offSite', { distance: formatDistance(entry.inDistanceMeters ?? 0) })}
      </span>
    );

  if (entries.length === 0 && !editable) return null;

  return (
    <div className="mt-4 border-t border-[color:var(--color-line)] pt-3">
      <p className="mb-2 text-sm font-semibold">{t('visitManage.timeClock.heading')}</p>
      <ul className="space-y-1 text-sm">
        {entries.map((entry) => {
          const start = zonedParts(entry.clockedInAt, timezone);
          const end = entry.clockedOutAt ? zonedParts(entry.clockedOutAt, timezone) : null;
          return (
            <li key={entry.id}>
              <div className="flex flex-wrap items-center gap-2">
                <span>
                  <span className="font-medium">{entry.userName}</span>
                  <span className="text-[color:var(--color-muted)]">
                    {' · '}
                    {t('visitManage.timeClock.clockedIn', { time: clock(entry.clockedInAt) })}
                    {entry.clockedOutAt
                      ? ` – ${t('visitManage.timeClock.clockedOut', { time: clock(entry.clockedOutAt) })}`
                      : ` · ${t('visitManage.timeClock.stillClockedIn')}`}
                    {entry.minutes !== null ? ` · ${formatMinutes(entry.minutes)}` : ''}
                  </span>
                </span>
                {badge(entry)}
                {entry.unpaid ? (
                  <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                    {t('jobProfit.time.unpaidBadge')}
                  </span>
                ) : null}
                {entry.editedAt ? (
                  <span className="text-xs text-[color:var(--color-muted)]">{t('jobProfit.time.edited')}</span>
                ) : null}
                {editable && editing !== entry.id ? (
                  <Button variant="ghost" onClick={() => setEditing(entry.id)}>
                    {entry.clockedOutAt ? t('jobProfit.time.edit') : t('jobProfit.time.clockOut')}
                  </Button>
                ) : null}
              </div>
              {editing === entry.id ? (
                <TimeEntryForm
                  timezone={timezone}
                  initial={{
                    date: start.date,
                    inTime: start.time,
                    outTime: end?.time ?? '',
                    unpaid: entry.unpaid,
                  }}
                  busy={update.isPending}
                  error={error}
                  onSave={(value) => update.mutate({ entryId: entry.id, ...value })}
                  onCancel={() => {
                    setEditing(null);
                    setError(null);
                  }}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
      {editable ? (
        adding ? (
          <TimeEntryForm
            timezone={timezone}
            initial={{
              date: zonedParts(new Date().toISOString(), timezone).date,
              inTime: '',
              outTime: '',
              unpaid: false,
            }}
            members={(team.data?.items ?? []).filter((member) => member.isActive)}
            busy={add.isPending}
            error={error}
            onSave={(value) => add.mutate({ ...value, userId: value.userId ?? '' })}
            onCancel={() => {
              setAdding(false);
              setError(null);
            }}
          />
        ) : (
          <Button variant="ghost" onClick={() => setAdding(true)}>
            {t('jobProfit.time.add')}
          </Button>
        )
      ) : null}
    </div>
  );
}

/**
 * Everything you can do to one visit, in one place: the calendar's answer to
 * "I clicked it — now what?".
 *
 * It carries the per-visit actions plus the one decision that belongs to the
 * schedule behind the visit rather than to the visit itself — canceling the
 * whole thing. Both live here because from the calendar the two are the same
 * question asked at different sizes ("not this Tuesday" versus "not any more"),
 * and sending someone to a different page to answer the second is how a
 * canceled customer keeps getting visits.
 */
export function VisitManagePanel({
  appointment,
  assigneeName,
  onChanged,
  onClose,
  startCompleting = false,
  startCanceling = false,
}: {
  appointment: Appointment;
  /** The crew's names, already joined, when the caller knows the team. */
  assigneeName?: string | null;
  onChanged: () => void;
  onClose?: () => void;
  /** Open the close-out form immediately (deep link from a completion nudge). */
  startCompleting?: boolean;
  startCanceling?: boolean;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  const visitPricingAllowed = usePlanFeature('scheduling');
  const extraWorkAllowed = usePlanFeature('invoicing');
  const queryClient = useQueryClient();
  // Whatever is changed from this panel can change what the job made: its
  // status, crew, time or payments. The profit card's own query is refreshed
  // with whatever the caller refreshes.
  const changed = () => {
    void queryClient.invalidateQueries({ queryKey: ['appointment-profit', appointment.id] });
    onChanged();
  };
  const hasMessaging = usePlanFeature('messaging');
  const [error, setError] = useState<string | null>(null);
  /** Whether the composer has been opened. Closed is the resting state. */
  const [messaging, setMessaging] = useState(false);

  /**
   * The calendar's list rows do not carry time entries — loading every
   * visit's punches would be a query per line — so the panel asks the detail
   * endpoint itself. A caller that already has them (the detail read, or a
   * clock endpoint's response) is believed as-is and no request goes out.
   */
  const detail = useQuery({
    queryKey: ['appointment', appointment.id],
    queryFn: () => scheduleApi.appointment(appointment.id),
    enabled: appointment.timeEntries === undefined,
  });
  const timeEntries = appointment.timeEntries ?? detail.data?.timeEntries ?? [];

  const cancelSeries = useMutation({
    mutationFn: () => scheduleApi.endSeries(appointment.seriesId),
    onSuccess: () => {
      setError(null);
      changed();
      // The quote this schedule claimed goes back to Accepted. The calendar
      // refresh does not touch the quote cache, so the badge would stay
      // Scheduled until a reload.
      invalidateQuoteViews(queryClient);
    },
    onError: (caught: unknown) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('visitManage.cancelScheduleError')),
  });

  // Shift swaps touching this visit (0095): a badge while one is open, and the
  // approval card here when it waits on the office.
  const hasStaffPortal = usePlanFeature('staff_portal');
  const swapApprover = hasStaffPortal && can('staff_portal:manage');
  const swaps = useQuery({
    queryKey: ['swaps', 'visit', appointment.id],
    queryFn: () => api.get<OfficeSwapListing>(`/swaps?appointmentId=${encodeURIComponent(appointment.id)}`),
    enabled: swapApprover,
    retry: false,
  });
  const openSwaps = (swaps.data?.items ?? []).filter(
    (item) => item.status === 'pending_peer' || item.status === 'pending_owner',
  );
  const decideSwap = useMutation({
    mutationFn: (input: { id: string; decision: 'approved' | 'declined'; note: string }) =>
      api.post(`/swaps/${input.id}/decision`, { decision: input.decision, note: input.note }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['swaps'] });
      changed();
    },
  });

  const writable = can('schedule:write');
  const hasJobProfit = usePlanFeature('job_profit');
  // Pay and profit are the owner's and managers' (`reports:read`); never a
  // crew role's, who see the visit and its punches and nothing more.
  const showProfit = hasJobProfit && can('reports:read');
  const officeTime = showProfit && writable;

  return (
    <section className="card schedule-visit-panel" aria-label={t('visitManage.ariaLabel')}>
      <VisitColorTags id={appointment.id} onChanged={changed} />
      <a className="btn btn-secondary mb-3" href={`/chats?visitId=${appointment.id}`}>
        {t('jobChat.open')}
      </a>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-[color:var(--color-muted)]">
            {t('visitManage.heading')}
          </p>
          <h2 className="text-lg font-semibold">
            {t('visitManage.whenTitle', {
              date: formatLongDate(appointment.scheduledDate),
              time: formatLocalTime(appointment.startTimeLocal),
            })}
          </h2>
          <p className="text-[color:var(--color-muted)]">
            {/* A new tab, so the customer's record sits beside the calendar
                instead of replacing it and losing the week or month in view. */}
            {can('customers:read') ? (
              <a
                href={`/customers/${appointment.customerId}`}
                target="_blank"
                rel="noopener"
                className="font-medium text-[color:var(--color-ink)] underline"
              >
                {appointment.customerName}
                <span aria-hidden="true"> ↗</span>
                <span className="sr-only"> {t('visitManage.opensInNewTab')}</span>
              </a>
            ) : (
              appointment.customerName
            )}
            {appointment.propertyAddress ? (
              <>
                {' · '}
                <AddressLink address={appointment.propertyAddress} />
              </>
            ) : null}
            {' · '}
            {t('visitManage.estimated', { duration: formatDuration(appointment.durationMinutes) })}
            {assigneeName ? ` · ${t('visitManage.assignedTo', { name: assigneeName })}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {appointment.paymentStatus !== 'not_billed' ? <PaymentBadge status={appointment.paymentStatus} /> : null}
          <AppointmentBadge status={appointment.status} />
          {openSwaps.length ? (
            <span className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
              {t('shiftSwaps.review.badge')}
            </span>
          ) : null}
          {onClose ? (
            <Button variant="ghost" onClick={onClose}>
              {t('visitManage.close')}
            </Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {openSwaps.map((item) =>
        item.status === 'pending_owner' ? (
          <div key={item.id} className="mb-3">
            <SwapApprovalCard
              item={item}
              review={swaps.data?.reviews[item.id]}
              busy={decideSwap.isPending}
              error={decideSwap.isError ? t('shiftSwaps.actionFailed') : null}
              onDecide={(decision, note) => decideSwap.mutate({ id: item.id, decision, note })}
            />
          </div>
        ) : null,
      )}
      {openSwaps.length ? (
        <p className="mb-3">
          <Link to="/staff/swaps" className="underline">
            {t('shiftSwaps.review.open')}
          </Link>
        </p>
      ) : null}

      {/* Worded generically on purpose: `failed` is the ledger's rollup for
          ANY charge attempt that didn't collect — a saved card declined, but
          also a texted checkout link or a Tap to Pay swipe that failed at the
          reader. "The card on file" is only sometimes the cause, so the copy
          doesn't assert it; the ledger below (inside AppointmentActions,
          gated the same way) is what actually says which attempt failed and
          why. The link itself is gated on `payments:write`, same as the
          controls at its destination (BillingCard) — offering a link to a
          page whose every button is disabled would be its own dead end. */}
      {appointment.paymentStatus === 'failed' ? (
        <div className="mb-3">
          <Alert tone="error" title={t('visitManage.failedPaymentTitle')}>
            <p className="mb-2">{t('visitManage.failedPaymentMessage')}</p>
            {can('payments:write') ? (
              <Link to={`/customers/${appointment.customerId}#billing-heading`} className="btn btn-secondary">
                {t('visitManage.updateCardOnFile')}
              </Link>
            ) : null}
          </Alert>
        </div>
      ) : null}

      {appointment.completionOverride ? (
        <Alert
          tone="warning"
          title={t('checklists.overrideFlag', { count: appointment.completionOverride.missingRequiredPhotos })}
        >
          <p>{appointment.completionOverride.reason}</p>
        </Alert>
      ) : null}

      {writable ? (
        <AppointmentActions
          startCanceling={startCanceling}
          appointment={appointment}
          onChanged={changed}
          startCompleting={startCompleting}
        />
      ) : null}

      {/* The time clock's capture surface is the phone in the cleaner's
          pocket; the office sees the record here, and the owner and managers
          can correct it (0035). */}
      <TimeClockSection
        appointmentId={appointment.id}
        entries={timeEntries}
        timezone={appointment.timezone ?? detail.data?.timezone ?? 'UTC'}
        editable={officeTime && appointment.status !== 'cancelled'}
        onChanged={changed}
      />

      {/* What the job made, or is expected to (0035). Owner and managers. */}
      {showProfit ? <JobProfitCard appointmentId={appointment.id} /> : null}
      {/* Sibling keys must differ. These two cards once shared the visit id as
          their key, and React reconciles siblings by key: when a card above
          them appeared or went away (the profit card, the price card's own
          plan check) the collision left a stale price card mounted beside the
          new one, so the panel showed "Price for this visit" twice. */}
      {/* Above the price it changes: "can you do the fridge too?" is asked
          with this panel open, so the visit is already chosen. */}
      {visitPricingAllowed && extraWorkAllowed && can('quotes:read') ? (
        <VisitExtraWorkCard key={`extra:${appointment.id}`} appointment={appointment} onChanged={changed} />
      ) : null}
      {visitPricingAllowed && can('payments:read') ? (
        <VisitPriceCard key={`price:${appointment.id}`} appointmentId={appointment.id} onChanged={changed} />
      ) : null}

      <VisitInstructionsCard
        key={`instructions:${appointment.id}`}
        appointmentId={appointment.id}
        closed={!OPEN_APPOINTMENT_STATUSES.includes(appointment.status)}
      />
      <VisitChecklist appointmentId={appointment.id} mode="panel" onChanged={changed} />

      {/* ---- Say something to the customer ------------------------------ */}
      {/* Here because this is the panel somebody has open when the reason to
          write appears: the crew is running late, the gate code did not work,
          the visit needs moving by half an hour. Sending them to the customer
          record to say so is how that message ends up going out of somebody's
          own phone instead — where the office never sees it and the next
          person to look reads a silence that never happened.

          Collapsed by default: it is the exception on this panel, not the
          first thing anybody came here to do. */}
      {writable && can('customers:write') && hasMessaging ? (
        <div className="mt-4 border-t border-[color:var(--color-line)] pt-3">
          {messaging ? (
            <MessageComposer customerId={appointment.customerId} />
          ) : (
            <Button variant="secondary" onClick={() => setMessaging(true)}>
              {t('visitManage.messageCustomer')}
            </Button>
          )}
        </div>
      ) : null}

      {/* The schedule behind the visit: everything here is about all of it. */}
      <div className="mt-4 border-t border-[color:var(--color-line)] pt-3">
        <p className="mb-2 text-sm font-semibold">{t('visitManage.wholeScheduleHeading')}</p>
        <div className="flex flex-wrap items-center gap-3">
          <Link to={`/schedule/series/${appointment.seriesId}`} className="btn btn-secondary">
            {t('visitManage.openWholeSchedule')}
          </Link>
          {writable ? (
            <Button
              variant="danger"
              busy={cancelSeries.isPending}
              onClick={() => {
                if (window.confirm(t('visitManage.confirmCancelSchedule'))) cancelSeries.mutate();
              }}
            >
              {t('visitManage.cancelSchedule')}
            </Button>
          ) : null}
          <span className="text-sm text-[color:var(--color-muted)]">{t('visitManage.cancelScheduleHint')}</span>
        </div>
      </div>
    </section>
  );
}
